import type { FC } from 'react';

import { Check } from 'lucide-react';

import { cn } from '@shared/utils';

import type { GenericMultiSelectOption } from './GenericMultiSelect';

interface MultiSelectOptionButtonProps {
  option: GenericMultiSelectOption;
  index: number;
  isSelected: boolean;
  isFocused: boolean;
  handleToggle: (val: string) => void;
  setFocusedIndex: (idx: number) => void;
  optionRef: (el: HTMLButtonElement | null) => void;
  disabled?: boolean;
}

export const MultiSelectOptionButton: FC<Readonly<MultiSelectOptionButtonProps>> = ({
  option,
  index,
  isSelected,
  isFocused,
  handleToggle,
  setFocusedIndex,
  optionRef,
  disabled,
}) => {
  return (
    <button
      ref={optionRef}
      type="button"
      onClick={() => !disabled && handleToggle(option.value)}
      className={cn(
        'relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer',
        isFocused && 'bg-accent text-accent-foreground',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle(option.value);
        }
      }}
      onMouseEnter={() => setFocusedIndex(index)}
      data-testid={`multi-select-option-button-root-${option.value}`}
      disabled={disabled}
    >
      <span
        className="flex-1 w-full text-left leading-snug"
        data-testid={`multi-select-option-button-label-${option.value}`}
      >
        {option.label}
      </span>
      {option.count !== undefined && <span className="text-xs text-muted-foreground shrink-0">({option.count})</span>}
      <span
        className="absolute right-2 flex size-3.5 items-center justify-center"
        data-testid={`multi-select-option-button-check-${option.value}`}
      >
        {isSelected && <Check className="size-4 text-primary" />}
      </span>
    </button>
  );
};
